import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import { extractErrorMessage } from '../Services/httpClient';
import { hasRole, ROLES } from '../Services/AuthService';
import { getSettings, saveSettings } from '../Services/CatalogApi';
import {
    boundOf, describeLastChange, getAt, listToText, MAX_TEST_ENTRIES, sameSettings, setAt, SETTINGS_SECTIONS, textToList, validateSettings
} from '../Services/settingsModel';

const ORANGE = '#f77513';
const formatDate = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR') : '');

/**
 * Configuração do catálogo de notificações: destinos do envio de teste, intervalos de consulta e limites de tamanho. Qualquer perfil do catálogo vê;
 * só o editor de consentimento altera, porque a lista decide quem recebe e-mail com a marca do serviço e os limites decidem o que pode ser publicado.
 * A tela confere as faixas, mas quem decide é o servidor, e a gravação leva a versão lida (duas pessoas editando não se atropelam).
 */
const ConfigurationPanel = () => {
    const canEdit = hasRole(ROLES.consentEditor);
    const [view, setView] = useState(null);        // { settings, version, updatedAt, updatedBy, defaults, bounds }
    const [draft, setDraft] = useState(null);      // os números em edição
    const [emailsText, setEmailsText] = useState('');
    const [profilesText, setProfilesText] = useState('');
    const [error, setError] = useState(null);
    const [info, setInfo] = useState(null);
    const [busy, setBusy] = useState(false);

    const adopt = useCallback((loaded) => {
        setView(loaded);
        setDraft(loaded.settings);
        setEmailsText(listToText(loaded.settings?.test?.emails));
        setProfilesText(listToText(loaded.settings?.test?.pushProfiles));
    }, []);

    const load = useCallback(async () => {
        setError(null);
        try {
            adopt(await getSettings());
        } catch (err) {
            setError(extractErrorMessage(err));
        }
    }, [adopt]);

    useEffect(() => { load(); }, [load]);

    // O que seria enviado: os números em edição mais as duas listas, lidas das caixas de texto.
    const current = useMemo(() => (draft
        ? { ...draft, test: { ...draft.test, emails: textToList(emailsText), pushProfiles: textToList(profilesText) } }
        : null), [draft, emailsText, profilesText]);

    const problems = useMemo(() => (current && view ? validateSettings(current, view.bounds) : []), [current, view]);
    const changed = !!current && !!view && !sameSettings(current, view.settings);

    const handleNumber = (path) => (e) => {
        const raw = e.target.value;
        setInfo(null);
        setDraft((d) => setAt(d, path, raw === '' ? null : Number(raw)));
    };

    const handleSave = async () => {
        setBusy(true);
        setError(null);
        setInfo(null);
        try {
            adopt(await saveSettings(current, view.version));
            setInfo('Configuração salva. Vale a partir de agora.');
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setBusy(false);
        }
    };

    const handleRestoreDefaults = () => {
        setInfo(null);
        setDraft(view.defaults);
        setEmailsText(listToText(view.defaults?.test?.emails));
        setProfilesText(listToText(view.defaults?.test?.pushProfiles));
    };

    const handleDiscard = () => {
        setInfo(null);
        adopt(view);
    };

    return (
        <Box>
            <Typography variant="h5" sx={{ color: ORANGE, fontWeight: 'bold', mb: 0.5 }}>Configuração do catálogo</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Destinos do envio de teste, intervalos de consulta e limites de tamanho dos textos.
                {view ? ` ${describeLastChange({ version: view.version, updatedAt: view.updatedAt, updatedBy: view.updatedBy }, formatDate)}` : ''}
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)} action={<Button color="inherit" size="small" onClick={load}>Recarregar</Button>}>
                    {error}
                </Alert>
            )}
            {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>{info}</Alert>}
            {!view && !error && <CircularProgress size={24} style={{ color: ORANGE }} />}
            {view && !canEdit && (
                <Alert severity="info" sx={{ mb: 2 }}>Você pode ver a configuração. Só o perfil de editor de consentimento a altera.</Alert>
            )}

            {view && draft && (
                <>
                    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: '14px' }}>
                        <Typography variant="subtitle1" sx={{ color: ORANGE, fontWeight: 'bold' }}>Destinos do envio de teste</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            Só estes destinos recebem o teste de um rascunho; nunca uma pessoa de verdade. Um por linha (até {MAX_TEST_ENTRIES} em cada lista).
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <TextField label="E-mails" multiline minRows={3} size="small" sx={{ flex: '1 1 280px' }} disabled={!canEdit || busy}
                                value={emailsText} onChange={(e) => { setInfo(null); setEmailsText(e.target.value); }} />
                            <TextField label="Perfis para o push (ids)" multiline minRows={3} size="small" sx={{ flex: '1 1 280px' }} disabled={!canEdit || busy}
                                value={profilesText} onChange={(e) => { setInfo(null); setProfilesText(e.target.value); }}
                                helperText="O push vai aos aparelhos em que esse perfil ativou as notificações." />
                        </Box>
                    </Paper>

                    {SETTINGS_SECTIONS.map((section) => (
                        <Paper key={section.id} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: '14px' }}>
                            <Typography variant="subtitle1" sx={{ color: ORANGE, fontWeight: 'bold' }}>{section.title}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{section.help}</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 2 }}>
                                {section.fields.map((field) => {
                                    const bound = boundOf(view.bounds, field.path);
                                    const value = getAt(draft, field.path);
                                    const range = bound ? `de ${bound.min} a ${bound.max}` : '';
                                    const invalid = !Number.isInteger(value) || (bound && (value < bound.min || value > bound.max));
                                    return (
                                        <TextField key={field.path} type="number" size="small" label={field.label} disabled={!canEdit || busy}
                                            value={value ?? ''} onChange={handleNumber(field.path)} error={invalid}
                                            inputProps={{ min: bound?.min, max: bound?.max, step: 1 }}
                                            helperText={[range, field.help].filter(Boolean).join(' · ')} />
                                    );
                                })}
                            </Box>
                        </Paper>
                    ))}

                    <Alert severity="info" sx={{ mb: 2 }}>
                        Baixar um teto não muda o que já está publicado, mas uma versão agendada ou republicada que passe do novo teto será recusada.
                    </Alert>

                    {canEdit && problems.length > 0 && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {problems.map((p) => <div key={p}>{p}</div>)}
                        </Alert>
                    )}

                    {canEdit && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button variant="contained" disabled={busy || !changed || problems.length > 0}
                                style={busy || !changed || problems.length > 0 ? undefined : { backgroundColor: ORANGE, color: 'white' }} onClick={handleSave}>
                                {busy ? 'Salvando...' : 'Salvar configuração'}
                            </Button>
                            <Button disabled={busy || !changed} onClick={handleDiscard} style={{ color: '#777' }}>Descartar alterações</Button>
                            <Button disabled={busy} onClick={handleRestoreDefaults} style={{ color: ORANGE }}>Restaurar os padrões</Button>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default ConfigurationPanel;
